import * as Sentry from "@sentry/react";

/**
 * Sentry (browser) initialisation.
 *
 * Reads the DSN from `VITE_SENTRY_DSN`. When the var is absent (e.g. a local
 * dev shell that hasn't set it) init is skipped, so Sentry never fires noise
 * from developer machines unless you explicitly opt in.
 *
 * Free-plan feature set: error capture + performance tracing + session replay.
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Don't leak text/inputs into replays by default.
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Low-traffic internal tool — full trace sampling stays well inside quota.
    tracesSampleRate: 1.0,
    // Replay: sample 10% of ordinary sessions, 100% of sessions with an error.
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

/**
 * Attach the signed-in user to Sentry events. Called with the resolved
 * `currentUser` (from useCurrentUser) once it's available; passing null clears
 * the user (e.g. on sign-out).
 */
export function setSentryUser(
  user: { id: string; email_address: string } | null
) {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  Sentry.setUser(
    user ? { id: user.id, email: user.email_address } : null
  );
}

export { Sentry };
